import { Check } from "lucide-react";
import { RevealOnScroll, FadeUp } from "../../lib/motion";
import { useLang } from "../../i18n/LanguageContext";

export const About = () => {
  const { t } = useLang();
  const a = t.about;

  return (
    <section
      id="agence"
      className="relative overflow-hidden bg-white"
      data-testid="about-section"
    >
      {/* Agent photo — background of the About section only */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="/agent.webp?v=3"
          alt=""
          className="w-full h-full object-cover object-top opacity-[0.09] grayscale"
          data-testid="about-bg"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/92 to-white" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40">
        {/* Header */}
        <FadeUp>
          <span className="text-sm md:text-base uppercase tracking-[0.25em] font-bold text-neutral-500">
            {a.label}
          </span>
        </FadeUp>
        <h2 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tighter mt-6">
          <RevealOnScroll>{a.name}</RevealOnScroll>
        </h2>
        <FadeUp delay={0.15}>
          <p className="mt-4 text-base md:text-lg uppercase tracking-[0.2em] font-bold text-neutral-600">
            {a.role}
          </p>
        </FadeUp>

        {/* Intro : portrait + text */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-16 md:mt-24 items-start">
          <FadeUp className="lg:col-span-5">
            <div className="border border-black overflow-hidden">
              <img
                src="/agent.webp?v=3"
                alt="Hugues MVOULE — Fondateur & Agent de Joueurs Licencié FIFA"
                className="w-full h-auto object-cover"
                data-testid="about-portrait"
              />
            </div>
          </FadeUp>

          <div className="lg:col-span-7">
            <FadeUp>
              <p className="font-display font-bold text-2xl md:text-3xl leading-tight tracking-tight text-black">
                {a.intro[0]}
              </p>
            </FadeUp>
            {a.intro.slice(1).map((p, i) => (
              <FadeUp key={i} delay={0.1 + i * 0.08}>
                <p className="mt-6 text-neutral-700 text-base md:text-lg leading-relaxed">
                  {p}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Vision */}
        <div className="mt-24 md:mt-36 border-t border-black pt-14 md:pt-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight tracking-tight">
              {a.visionTitle}
            </h3>
          </div>
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="text-neutral-700 text-lg md:text-xl leading-relaxed">
                {a.visionLead}
              </p>
            </FadeUp>
            <div className="mt-8 space-y-2">
              {a.visionPoints.map((p, i) => (
                <RevealOnScroll key={i} delay={i * 0.08}>
                  <span className="block font-display font-bold uppercase text-xl md:text-2xl tracking-tight">
                    {p}
                  </span>
                </RevealOnScroll>
              ))}
            </div>
            {a.visionBody.map((p, i) => (
              <FadeUp key={i} delay={0.1 + i * 0.08}>
                <p className="mt-6 text-neutral-700 text-base md:text-lg leading-relaxed">
                  {p}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="mt-24 md:mt-36">
          <FadeUp>
            <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight tracking-tight mb-12 md:mb-16">
              {a.valuesTitle}
            </h3>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.values.map((v, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="border border-black p-7 md:p-8 h-full bg-white/70 backdrop-blur-sm">
                  <div className="font-display font-medium text-3xl text-neutral-300">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h4 className="mt-4 font-display font-bold uppercase text-xl md:text-2xl tracking-tight">
                    {v.title}
                  </h4>
                  <p className="mt-4 text-neutral-700 text-sm md:text-base leading-relaxed">
                    {v.text}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Network */}
        <div className="mt-24 md:mt-36 border-t border-black pt-14 md:pt-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight tracking-tight">
              {a.networkTitle}
            </h3>
            <div className="mt-8">
              {a.networkBody.map((p, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <p className="mt-6 first:mt-0 text-neutral-700 text-base md:text-lg leading-relaxed">
                    {p}
                  </p>
                </FadeUp>
              ))}
            </div>
          </div>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <div className="overflow-hidden" data-testid="network-image-wrap">
              <img
                src="/network.png"
                alt="Réseau international de contacts de HM Sport Consulting"
                className="w-full h-auto"
                data-testid="network-image"
              />
            </div>
          </FadeUp>
        </div>

        {/* Closing */}
        <div className="mt-24 md:mt-36 border-t border-black pt-14 md:pt-20">
          <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight tracking-tight max-w-3xl">
            <RevealOnScroll>{a.closingTitle}</RevealOnScroll>
          </h3>
          <div className="mt-8 grid lg:grid-cols-2 gap-8 max-w-4xl">
            {a.closingBody.map((p, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <p className="text-neutral-700 text-base md:text-lg leading-relaxed">
                  {p}
                </p>
              </FadeUp>
            ))}
          </div>

          <div className="mt-14 border-t border-black pt-10">
            <p className="font-display font-black uppercase text-2xl md:text-3xl leading-tight tracking-tight">
              {a.tagline.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < a.tagline.length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>
        </div>

        {/* Trust */}
        <div className="mt-24 md:mt-32 bg-black text-white p-8 md:p-14">
          <FadeUp>
            <h3 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight mb-10">
              {a.trustTitle}
            </h3>
          </FadeUp>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
            {a.trust.map((item, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <li className="flex items-start gap-4 border-b border-white/15 pb-5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-white">
                    <Check size={14} />
                  </span>
                  <span className="text-base md:text-lg text-white/90 leading-snug">
                    {item}
                  </span>
                </li>
              </FadeUp>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default About;
